import React, { useState, useEffect } from 'react';
import { useReactiveVar, useQuery, gql } from '@apollo/client';
import {
  Dropdown,
  Toggle,
  Grid,
  Icon,
  Tooltip,
  Typography,
  RadioGroup,
  RadioButton,
  Card,
  CardHeader,
  CardBody,
  ButtonIcon,
} from '@statsbomb/kitbag-components';
import { isEmpty } from 'lodash';
import { useTheme } from 'styled-components';
import TableTile from '../../../components/TableTile/TableTile';
import RadarChart from '../../../visualisations/RadarChart/RadarChart';
import { mf_PlayerDetails, mf_TeamDetails } from '../../../apollo';
import { ERROR_CODES } from '../../../utils/errorCodes/errorCodes';
import Tile from '../../../components/Tile/Tile';
import AccordionTile from '../../../components/Accordion/AccordionTile';
import useQueryString from '../../../utils/hooks/useQueryString';
import {
  getRadarStatsLeagueAverageQuery,
  getRadarStatsQuery,
} from './getPlayerRadarStats';
import { useGetTemplateDefinitions } from './PlayerRadar.hooks';
import {
  formatTemplateStatsAsRadarAxes,
  getKeyInfo,
  getTableData,
  getTemplateDropdownOptions,
} from './PlayerRadar.dataManipulation';
import { RadarExportContainer } from './PlayerRadar.styles';
import KeyButton from '../../../components/buttons/DisplayKey/DisplayKey';
import {
  CardBodyWrapper,
  FixedAside,
  FixedAsideContent,
  GridBackground,
  KitbagPageGridHolder,
} from '../../../components/Layout/Layout.styles';
import ExportModal from '../../../components/ExportModal/ExportModal';
import { useExportSeasonInfo } from '../../../utils/hooks/useExportSeasonInfo';
import { useGetPlayerStatPositionUsage } from '../../../utils/hooks/useGetPlayerStatPositionUsage';
import PositionFilter from '../../../components/PositionFilter/PositionFilter';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { DropdownOverride } from '../../../components/DropdownFilter/DropdownFilter.styles';
import {
  RADAR_DISPLAY_OPTIONS,
  RADAR_SIDEPANEL_WIDTH,
  RADAR_TOOLTIP_DELAY,
} from './PlayerRadar.constants';
import { TooltipInline } from '../../../components/Tooltip/Tooltip.styles';
import { ButtonIconWrapper } from '../../../components/buttons/Buttons.styles';
import {
  getRadarCardState,
  getRadarExportType,
  info2Copy,
  radarPositionsTooltipContent,
  radarTemplateTooltipContent,
} from './PlayerRadar.helpers';

const PlayerRadar = () => {
  const theme = useTheme();
  // selected player
  const mf_PlayerDetailsRV = useReactiveVar(mf_PlayerDetails);
  const mf_TeamDetailsRV = useReactiveVar(mf_TeamDetails);
  const playerName = mf_PlayerDetailsRV?.name;
  const playerId = mf_PlayerDetailsRV?.id || 0;
  const { competitionSeason: seasonInfo, seasonName } = useExportSeasonInfo({
    asObject: true,
  });

  // filtering
  const [showLeagueAverage, setShowLeagueAverage] = useQueryString(
    'average',
    false
  );
  const [displayKeyTrait, setDisplayKeyTrait] = useQueryString(
    'keyTrait',
    true
  );
  const [displayKeyPerf, setDisplayKeyPerf] = useQueryString('keyPerf', true);
  const { PERFORMANCE, TRAIT, BOTH } = RADAR_DISPLAY_OPTIONS;
  const [showRadarType, setShowRadarType] = useQueryString('radarType', BOTH);
  const [showTableTrait, setShowTableTrait] = useQueryString(
    'tableTrait',
    true
  );
  const [showTablePerf, setShowTablePerf] = useQueryString('tablePerf', true);
  const [allPlays, setAllPlays] = useQueryString('all', true);

  // alignment position for queries etc.
  const [alignmentPositionsApiCodes, setAlignmentPositionsApiCodes] = useState(
    []
  );

  /* Position Usages ~ how many plays per position a player was in ~ for alignment filter */
  const { playerStatsData, loading: isPlayerPositionUsageLoading } =
    useGetPlayerStatPositionUsage({
      selectedPlayerId: playerId,
      useContextFilters: false,
    });
  const positionUsages =
    playerStatsData?.length > 0 ? playerStatsData[0].positions : [];

  const {
    templates,
    loading: isConfigLoading,
    error: configError,
  } = useGetTemplateDefinitions();

  /* variant formatting for the drop down list */
  const templateOptions = getTemplateDropdownOptions(templates);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showNoTemplateError, setShowNoTemplateError] = useState(false);
  const [useTeamColors, setUseTeamColors] = useQueryString('team', true);

  const teamColors = useTeamColors
    ? {
        primary: mf_TeamDetailsRV?.colorPrimary,
        secondary: mf_TeamDetailsRV?.colorSecondary,
        tertiary: mf_TeamDetailsRV?.colorTertiary,
      }
    : null;

  /* Can't set the default selectedTemplate until you have the player's details */
  useEffect(() => {
    const playerRole = mf_PlayerDetailsRV?.mostCommonPosition?.generalPosition;

    const defaultTemplate =
      playerRole && templates?.find((t) => t.defaultFor.includes(playerRole));
    if (defaultTemplate?.name) {
      setSelectedTemplateName(defaultTemplate?.name);
      setShowNoTemplateError(false);
    } else {
      setSelectedTemplateName('None');
      setShowNoTemplateError(true);
    }
  }, [mf_PlayerDetailsRV, isConfigLoading]);

  /* Selected template and stats */
  const selectedTemplate = templates?.find(
    (t) => t.name === selectedTemplateName
  );
  const templateNames = selectedTemplate?.stats
    ?.concat(selectedTemplate?.secondaryStats)
    ?.map((m) => m.name);

  // Player position chips
  const specificPositions = selectedTemplate?.specificPositions;
  const defaultRosterPositions = selectedTemplate?.defaultFor;
  useEffect(() => {
    if (!isEmpty(specificPositions)) {
      setAlignmentPositionsApiCodes(specificPositions);
    }
  }, [specificPositions]);

  /* From the stats, get the player and League Average values */
  const playerStatsGQLString = getRadarStatsQuery(templateNames);
  const playerStatsLAGQLString = getRadarStatsLeagueAverageQuery(templateNames);

  // Player Stats
  const {
    data: dataStats,
    loading: isStatsLoading,
    error: statsError,
  } = useQuery(gql(playerStatsGQLString), {
    variables: {
      specificPositions: allPlays ? [] : alignmentPositionsApiCodes,
    },
    headers: {
      'Accept-Version': 'v2023q2',
    },
  });
  if (statsError) {
    console.error(ERROR_CODES.GET_RADAR_STATS, statsError);
  }

  const hasError = !!configError || !!statsError;
  const isLoading =
    isConfigLoading || isStatsLoading || isPlayerPositionUsageLoading;

  // League Average
  const {
    data: dataStatsLA,
    loading: isStatsLoadingLA,
    error: statsLAError,
  } = useQuery(gql(playerStatsLAGQLString), {
    skip: !showLeagueAverage,
    variables: {
      specificPositions: allPlays ? [] : alignmentPositionsApiCodes,
      rosterPositions: allPlays ? defaultRosterPositions : [],
    },
    headers: {
      'Accept-Version': 'v2023q2',
    },
  });
  if (statsLAError) {
    console.error(ERROR_CODES.GET_RADAR_STATS_LA, statsLAError);
  }

  const radarStats = dataStats?.playerStats?.items?.[0];
  const radarStatsLA = dataStatsLA?.playerStats?.items?.[0];

  // Extract trait and performance data
  const traitTemplate = selectedTemplate?.secondaryStats?.map((m) => m.name);
  const perfTemplate = selectedTemplate?.stats?.map((m) => m.name);
  const radarTraitStats = {};
  const radarPerfStats = {};
  const radarTraitStatsLA = {};
  const radarPerfStatsLA = {};

  // prepare player data separate from the long league average query
  if (dataStats && selectedTemplate) {
    traitTemplate.forEach((attr) => {
      radarTraitStats[attr] = radarStats[attr];
    });
    radarTraitStats.plays = radarStats?.plays;

    perfTemplate.forEach((attr) => {
      radarPerfStats[attr] = radarStats[attr];
    });
    radarPerfStats.plays = radarStats?.plays;
  }

  if (dataStatsLA && selectedTemplate) {
    traitTemplate.forEach((attr) => {
      radarTraitStatsLA[attr] = radarStatsLA[attr];
    });
    radarTraitStatsLA.plays = radarStatsLA?.plays;

    perfTemplate.forEach((attr) => {
      radarPerfStatsLA[attr] = radarStatsLA[attr];
    });
    radarPerfStatsLA.plays = radarStatsLA?.plays;
  }

  const radarTraitComparison = showLeagueAverage && radarTraitStatsLA;
  const radarPerfComparison = showLeagueAverage && radarPerfStatsLA;

  const templateConfigTrait = formatTemplateStatsAsRadarAxes(
    selectedTemplate?.secondaryStats
  );
  const templateConfigPerf = formatTemplateStatsAsRadarAxes(
    selectedTemplate?.stats
  );

  // Trait Tabular Information
  const {
    radarTableData: traitTableData,
    radarTableHeaders: traitTableHeaders,
  } = getTableData(
    templateConfigTrait,
    showLeagueAverage,
    radarTraitStats,
    radarTraitComparison
  );
  // Performance Tabular Information
  const { radarTableData: perfTableData, radarTableHeaders: perfTableHeaders } =
    getTableData(
      templateConfigPerf,
      showLeagueAverage,
      radarPerfStats,
      radarPerfComparison
    );

  // Trait Key Inf
  const traitKeyInfo = displayKeyTrait
    ? getKeyInfo(
        allPlays ? [] : alignmentPositionsApiCodes,
        allPlays ? defaultRosterPositions : [],
        playerName,
        mf_TeamDetailsRV?.name,
        radarTraitStats,
        showLeagueAverage ? radarTraitComparison : null
      )
    : null;
  // Performance Key Info
  const perfKeyInfo = displayKeyPerf
    ? getKeyInfo(
        allPlays ? [] : alignmentPositionsApiCodes,
        allPlays ? defaultRosterPositions : [],
        playerName,
        mf_TeamDetailsRV?.name,
        radarPerfStats,
        showLeagueAverage ? radarPerfComparison : null
      )
    : null;

  const templateLabel = (
    <>
      <span>Radar Template</span>
      <TooltipInline>
        <Tooltip content={radarTemplateTooltipContent} placement="top-centre">
          <Icon variant="Info" size="small" colour="primary.main" />
        </Tooltip>
      </TooltipInline>
    </>
  );

  return (
    <KitbagPageGridHolder>
      <Grid container={false} page>
        <FixedAside
          $width={`${RADAR_SIDEPANEL_WIDTH}px`}
          $padding="1rem 0 6.5rem 0"
          $paddingTopMD="1rem"
        >
          <GridBackground>
            <AccordionTile
              header={<Tile.AccordionHeader>General</Tile.AccordionHeader>}
              body={
                <Tile.AccordionBody $gap="0.5rem">
                  <DropdownOverride>
                    <Dropdown
                      id="template-choice-dropdown"
                      options={templateOptions}
                      label={templateLabel}
                      onChange={(selectedOption) => {
                        setShowNoTemplateError(false);
                        setSelectedTemplateName(selectedOption.value);
                      }}
                      value={templateOptions.find(
                        (opt) => opt.value === selectedTemplateName
                      )}
                      isSearchable={false}
                      isLoading={!templateOptions || isLoading}
                      menuPosition="static"
                      size="small"
                    />
                  </DropdownOverride>

                  <div style={{ marginTop: theme.sizing.small }}>
                    <RadioGroup
                      groupId="radar-colors-group"
                      groupTitle={
                        <>
                          Position Filters
                          <TooltipInline>
                            <Tooltip
                              content={radarPositionsTooltipContent}
                              placement="bottom-centre"
                            >
                              <Icon
                                variant="Info"
                                size="small"
                                colour="primary.main"
                              />
                            </Tooltip>
                          </TooltipInline>
                        </>
                      }
                      size="regular"
                    >
                      <RadioButton
                        id="all-plays-toggle"
                        checked={allPlays}
                        disabled={isLoading}
                        label="All plays"
                        onChange={() => setAllPlays(true)}
                        size="small"
                      />
                      <RadioButton
                        id="by-alignment-position-toggle"
                        checked={!allPlays}
                        disabled={isLoading}
                        label="By alignment position"
                        onChange={() => setAllPlays(false)}
                        size="small"
                      />
                    </RadioGroup>
                  </div>

                  {!allPlays && (
                    <PositionFilter
                      selectedPositions={alignmentPositionsApiCodes}
                      setSelectedPositions={setAlignmentPositionsApiCodes}
                      isRosterPositions={false}
                      positionUsages={positionUsages}
                      showTitle={false}
                    />
                  )}
                </Tile.AccordionBody>
              }
              isExpandedDefault
              isMount
            />

            <AccordionTile
              header={
                <Tile.AccordionHeader>Display Settings</Tile.AccordionHeader>
              }
              body={
                <Tile.AccordionBody $gap="0.5rem">
                  <Typography variant="headingMedium">Radar Options</Typography>

                  <Toggle
                    id="league-average-toggle"
                    label={
                      isStatsLoadingLA
                        ? 'Loading league average'
                        : 'Show league average'
                    }
                    onChange={() => setShowLeagueAverage(!showLeagueAverage)}
                    checked={showLeagueAverage}
                    disabled={isStatsLoadingLA}
                    size="small"
                    data-testid="league-average-toggle"
                  />

                  <div style={{ marginTop: theme.sizing.small }}>
                    <RadioGroup
                      groupId="radar-colors-group"
                      groupTitle="Radar Colors"
                      size="regular"
                    >
                      <RadioButton
                        id="system-color-radar"
                        checked={!useTeamColors}
                        disabled={isLoading}
                        label="System"
                        onChange={() => setUseTeamColors(false)}
                        size="small"
                      />
                      <RadioButton
                        id="team-color-radar"
                        checked={useTeamColors}
                        disabled={isLoading}
                        label="Team"
                        onChange={() => setUseTeamColors(true)}
                        size="small"
                      />
                    </RadioGroup>
                  </div>

                  <div style={{ marginTop: theme.sizing.small }}>
                    <RadioGroup
                      groupId="radar-type-group"
                      groupTitle="Radar Type"
                      size="regular"
                    >
                      <RadioButton
                        id="radar-type-both"
                        checked={showRadarType === BOTH}
                        disabled={isLoading}
                        label="Both"
                        onChange={() => setShowRadarType(BOTH)}
                        size="small"
                      />
                      <RadioButton
                        id="radar-type-trait"
                        checked={showRadarType === TRAIT}
                        disabled={isLoading}
                        label="Trait"
                        onChange={() => setShowRadarType(TRAIT)}
                        size="small"
                      />
                      <RadioButton
                        id="radar-type-performance"
                        checked={showRadarType === PERFORMANCE}
                        disabled={isLoading}
                        label="Performance"
                        onChange={() => setShowRadarType(PERFORMANCE)}
                        size="small"
                      />
                    </RadioGroup>
                  </div>
                </Tile.AccordionBody>
              }
              isExpandedDefault
              isMount
            />
          </GridBackground>
        </FixedAside>
        <FixedAsideContent
          $margin="0.5rem 0.5rem 0 0"
          $marginMd={`0.5rem 0.5rem -3.5px ${RADAR_SIDEPANEL_WIDTH - 2}px`}
        >
          <Grid container={false}>
            <Grid item xs={12}>
              <PageHeader
                href="/player/overview/:leagues/:seasons/:teams/:players"
                rootPage="Player"
                activePage="Radar"
                showPlayerName
              >
                <div className="buttons">
                  <Tooltip
                    content={`Export ${getRadarExportType(
                      showRadarType
                    )} radar${showRadarType === BOTH ? 's' : ''} as an image`}
                    placement="left-centre"
                    delay={RADAR_TOOLTIP_DELAY}
                    isCondensed={false}
                  >
                    <ExportModal
                      title={playerName}
                      secondaryTitle={selectedTemplateName}
                      info1={`${showRadarType !== PERFORMANCE ? 'Trait' : ''} ${
                        showRadarType === BOTH ? '/' : ''
                      } ${showRadarType !== TRAIT ? 'Performance' : ''} ${
                        showRadarType === BOTH ? 'Radars' : 'Radar'
                      }`}
                      info2={info2Copy(allPlays, showLeagueAverage)}
                      info3={seasonInfo}
                      columns={showRadarType === BOTH ? 2 : 1}
                      fileName={`${playerName}-${selectedTemplateName}-Radar ${seasonName}`}
                      isDisabled={isLoading || hasError}
                    >
                      <RadarExportContainer>
                        {showRadarType !== PERFORMANCE &&
                          templateConfigTrait && (
                            <RadarChart
                              radarId="radar-trait-export-2"
                              templateConfig={templateConfigTrait}
                              radarStatDatum={radarTraitStats}
                              radarComparisonDatum={radarTraitComparison}
                              teamColors={teamColors}
                              keyInfo={traitKeyInfo}
                            />
                          )}
                        {showRadarType !== TRAIT && templateConfigPerf && (
                          <RadarChart
                            radarId="radar-performance-export-2"
                            templateConfig={templateConfigPerf}
                            radarStatDatum={radarPerfStats}
                            radarComparisonDatum={radarPerfComparison}
                            teamColors={teamColors}
                            keyInfo={perfKeyInfo}
                          />
                        )}
                      </RadarExportContainer>
                    </ExportModal>
                  </Tooltip>
                </div>
              </PageHeader>
            </Grid>

            {(showRadarType === BOTH || showRadarType === TRAIT) && (
              <Grid item sm={12} md={12} lg={showRadarType === BOTH ? 6 : 12}>
                <Card>
                  <CardHeader>
                    <Typography variant="headingMedium">Trait</Typography>
                    <ButtonIconWrapper>
                      {!hasError && !isLoading && (
                        <>
                          <Tooltip
                            content={`${
                              showTableTrait ? 'Hide' : 'Show'
                            } radar table`}
                            placement="top-centre"
                            delay={RADAR_TOOLTIP_DELAY}
                            isCondensed={false}
                          >
                            <ButtonIcon
                              onClick={() => setShowTableTrait(!showTableTrait)}
                              id="show-table-trait-button"
                              variant="secondary"
                              size="small"
                              off={!showTableTrait}
                              icon="TableView"
                            />
                          </Tooltip>
                          <Tooltip
                            content={`${
                              displayKeyTrait ? 'Hide' : 'Show'
                            } radar key`}
                            placement="top-centre"
                            delay={RADAR_TOOLTIP_DELAY}
                            isCondensed={false}
                          >
                            <KeyButton
                              showKey={displayKeyTrait}
                              handleShowKey={() =>
                                setDisplayKeyTrait(!displayKeyTrait)
                              }
                            />
                          </Tooltip>
                        </>
                      )}
                    </ButtonIconWrapper>
                  </CardHeader>
                  <CardBodyWrapper>
                    <CardBody
                      statusType={
                        getRadarCardState(
                          isLoading,
                          hasError,
                          radarTraitStats,
                          'trait'
                        ).state
                      }
                      statusTitle={
                        getRadarCardState(
                          isLoading,
                          hasError,
                          radarTraitStats,
                          'trait'
                        ).title
                      }
                      statusDescription={
                        getRadarCardState(
                          isLoading,
                          hasError,
                          radarTraitStats,
                          'trait'
                        ).description
                      }
                    >
                      <Typography variant="headingMedium">Radar</Typography>
                      <div
                        style={{
                          maxWidth: '600px',
                          margin: `0 auto ${theme.sizing.small} auto`,
                        }}
                      >
                        <RadarChart
                          radarId="radar-trait"
                          templateConfig={templateConfigTrait}
                          radarStatDatum={radarTraitStats}
                          radarComparisonDatum={radarTraitComparison}
                          teamColors={teamColors}
                          keyInfo={traitKeyInfo}
                        />
                      </div>
                      {showTableTrait && traitTableData && (
                        <>
                          <TableTile
                            data={traitTableData}
                            columnHeaders={traitTableHeaders}
                            showColumnHeader
                            fileName={`${playerName}-trait-data-${seasonName}.csv`}
                            iconSize="small"
                            iconStyle={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                            }}
                            margin="0 0 0.25rem 0"
                            tileTitle="Table"
                            withBorder
                          />
                        </>
                      )}
                    </CardBody>
                  </CardBodyWrapper>
                </Card>
              </Grid>
            )}

            {(showRadarType === BOTH || showRadarType === PERFORMANCE) && (
              <Grid item sm={12} md={12} lg={showRadarType === BOTH ? 6 : 12}>
                <Card>
                  <CardHeader>
                    <Typography variant="headingMedium">Performance</Typography>
                    <ButtonIconWrapper>
                      {!hasError && !isLoading && (
                        <>
                          <Tooltip
                            content={`${
                              showTablePerf ? 'Hide' : 'Show'
                            } radar table`}
                            placement="top-centre"
                            delay={RADAR_TOOLTIP_DELAY}
                            isCondensed={false}
                          >
                            <ButtonIcon
                              onClick={() => setShowTablePerf(!showTablePerf)}
                              id="show-table-trait-button"
                              variant="secondary"
                              size="small"
                              off={!showTablePerf}
                              icon="TableView"
                            />
                          </Tooltip>
                          <Tooltip
                            content={`${
                              displayKeyPerf ? 'Hide' : 'Show'
                            } radar key`}
                            placement="top-centre"
                            delay={RADAR_TOOLTIP_DELAY}
                            isCondensed={false}
                          >
                            <KeyButton
                              showKey={displayKeyPerf}
                              handleShowKey={() =>
                                setDisplayKeyPerf(!displayKeyPerf)
                              }
                            />
                          </Tooltip>
                        </>
                      )}
                    </ButtonIconWrapper>
                  </CardHeader>
                  <CardBodyWrapper>
                    <CardBody
                      statusType={
                        getRadarCardState(
                          isLoading,
                          hasError,
                          radarPerfStats,
                          'performance'
                        ).state
                      }
                      statusTitle={
                        getRadarCardState(
                          isLoading,
                          hasError,
                          radarPerfStats,
                          'performance'
                        ).title
                      }
                      statusDescription={
                        getRadarCardState(
                          isLoading,
                          hasError,
                          radarPerfStats,
                          'performance'
                        ).description
                      }
                    >
                      <Typography variant="headingMedium">Radar</Typography>
                      <div
                        style={{
                          maxWidth: '600px',
                          margin: `0 auto ${theme.sizing.small} auto`,
                        }}
                      >
                        <RadarChart
                          radarId="radar-performance"
                          templateConfig={templateConfigPerf}
                          radarStatDatum={radarPerfStats}
                          radarComparisonDatum={radarPerfComparison}
                          teamColors={teamColors}
                          keyInfo={perfKeyInfo}
                        />
                      </div>
                      {showTablePerf && perfTableData && (
                        <TableTile
                          data={perfTableData}
                          columnHeaders={perfTableHeaders}
                          showColumnHeader
                          fileName={`${playerName}-performance-data-${seasonName}.csv`}
                          iconSize="small"
                          iconStyle={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                          }}
                          margin="0 0 0.25rem 0"
                          tileTitle="Table"
                          withBorder
                        />
                      )}
                    </CardBody>
                  </CardBodyWrapper>
                </Card>
              </Grid>
            )}
          </Grid>
        </FixedAsideContent>
      </Grid>
    </KitbagPageGridHolder>
  );
};

export default PlayerRadar;
